import React, { useMemo, useState, useEffect } from "react";
import { Form, Input, InputNumber, Select, Switch, Card, Row, Col, Button, Space, Table, Tag, message, Modal, Radio } from "antd";
import { PlusOutlined, ArrowUpOutlined, ArrowDownOutlined, DeleteOutlined, SortAscendingOutlined, EditOutlined } from "@ant-design/icons";
import { useList } from "@refinedev/core";
import { TEAM_LIST_QUERY } from "../../teams/queries";
import axios from "axios";
import WorkflowDiagram from "./WorkflowDiagram";

type StepRow = {
  order: number;
  roleSlug: string;
  stepName?: string;
};

type Props = {
  // Parent should render <Form ... /> and pass its instance via this prop
  record?: any; // For edit mode
};

export const PurchaseTemplateForm: React.FC<Props> = ({  record }) => {
  // console.log({resource})
   const form = Form.useFormInstance();
  const [stepForm] = Form.useForm();
  const [isStepModalVisible, setIsStepModalVisible] = useState(false);
  const [editingStepIndex, setEditingStepIndex] = useState<number | null>(null);
  const [approvalType, setApprovalType] = useState<'LOCAL' | 'EXTERNAL'>('EXTERNAL');
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<any>(null);
  // Teams for teamId select
  const { data: teamsData, isLoading: teamsLoading, isError: teamsError } = useList<any>({
    resource: "teams",
    meta: { gqlQuery: TEAM_LIST_QUERY },
  });

  // GraphQL list may return either a flat array or { teams: { nodes: [] } }
  const teams =
    Array.isArray((teamsData as any)?.data)
      ? ((teamsData as any)?.data as any[])
      : ((teamsData as any)?.data?.teams?.nodes || []);

  const fetchWorkflows = async () => {
    try {
      const res = await axios.get(`${(import.meta as any).env.VITE_USE_FLOW_API_URL}/workflows`, { withCredentials: true });
      console.log(res)
      setWorkflows(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (approvalType === 'EXTERNAL') {
      fetchWorkflows();
    }
  }, [approvalType]);

  useEffect(() => {
    if (record) {
      console.log(record)
      if (record.approvalType) {
        setApprovalType(record.approvalType);
        form.setFieldsValue({ approvalType: record.approvalType });
      }
      if (record.externalWorkflow) {
        setSelectedWorkflow(record.externalWorkflow);

        form.setFieldsValue({ externalWorkflow: record.externalWorkflow });
      }
    }
  }, [record, form]);

  useEffect(() => {
    form.setFieldsValue({ approvalType, externalWorkflow: selectedWorkflow });
  }, [approvalType, selectedWorkflow, form]);

  const steps: StepRow[] = Form.useWatch("steps", {form, preserve: true }) || [];

  // console.log("Steps in form:", steps);
  const sortedSteps = useMemo(() => {
    return [...(steps || [])].sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0));
  }, [steps]);

  const addStep = () => {
    stepForm.resetFields();

  //  form.setFieldValue("fiscalYear", 3000); // Ensure form is updated
 //   console.log("form", form.getFieldsValue());
    setEditingStepIndex(null);
    setIsStepModalVisible(true);
  };

  const editStep = (index: number) => {
    const step = sortedSteps[index];
    stepForm.setFieldsValue({
      roleSlug: step.roleSlug,
      stepName: step.stepName,
    });
    setEditingStepIndex(index);
    setIsStepModalVisible(true);
  };

  const saveStep = () => {
    stepForm.validateFields()
      .then(values => {
        let step: StepRow;
        if (editingStepIndex !== null) {
          // Update existing step
          step = {
            order: sortedSteps[editingStepIndex].order, // Keep existing order
            roleSlug: values.roleSlug,
            stepName: values.stepName,
          };
          const newSteps = [...steps];
          // Find the original step in the unsorted 'steps' array and update it
          const originalStepIndex = steps.findIndex(s => s.order === sortedSteps[editingStepIndex].order && s.roleSlug === sortedSteps[editingStepIndex].roleSlug);
          if (originalStepIndex !== -1) {
            newSteps[originalStepIndex] = step;
          }
          form.setFieldsValue({ steps: newSteps });
          setEditingStepIndex(null);
        } else {
          // Add new step
          const newOrder = (steps.length > 0 ? Math.max(...steps.map(s => s.order)) : 0) + 1;
          step = {
            order: newOrder,
            roleSlug: values.roleSlug,
            stepName: values.stepName,
          };
          form.setFieldValue("steps", [...steps, step]);
        }
        stepForm.resetFields();
        setIsStepModalVisible(false);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  const removeStep = (index: number) => {
    const current = steps || [];
    const next = [...current];
    next.splice(index, 1);
    form.setFieldsValue({ steps: next });
  };

  const moveStep = (index: number, dir: -1 | 1) => {
    const current = steps || [];
    if (!current.length) return;
    const target = index + dir;
    if (target < 0 || target >= current.length) return;
    const next = [...current];
    const [a, b] = [next[index], next[target]];
    // swap order numbers
    const tmp = a.order;
    a.order = b.order;
    b.order = tmp;
    // swap positions so UI reflects immediately
    next[index] = b;
    next[target] = a;
    form.setFieldsValue({ steps: next });
  };

  const sortByOrder = () => {
    if (!steps?.length) return;
    const next = [...steps].sort((x, y) => Number(x.order ?? 0) - Number(y.order ?? 0));
    form.setFieldsValue({ steps: next });
    message.success("Steps sorted by order");
  };

  const columns: any[] = [
    {
      title: "Order",
      dataIndex: "order",
      width: 90,
      render: (text: any) => text, // Display only, editing in modal
    },
    {
      title: "Role Slug",
      dataIndex: "roleSlug",
      render: (text: any) => text, // Display only, editing in modal
    },
    {
      title: "Step Name",
      dataIndex: "stepName",
      render: (text: any) => text, // Display only, editing in modal
    },
    {
      title: "Actions",
      width: 160,
      render: (_: any, _r: StepRow, index: number) => (
        <Space>
          <Button size="small" icon={<ArrowUpOutlined />} onClick={() => moveStep(index, -1)} />
          <Button size="small" icon={<ArrowDownOutlined />} onClick={() => moveStep(index, +1)} />
          <Button size="small" icon={<EditOutlined />} onClick={() => editStep(index)} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeStep(index)} />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card title="Template Details" style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="name"
              label="Template Name"
              rules={[{ required: true, message: "Please enter a template name" }]}
            >
              <Input placeholder="Enter template name" />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="templateKind"
              label="Template Type"
              initialValue="REQUEST"
            >
              <Select placeholder="Select template type">
                <Select.Option value="REQUEST">Request</Select.Option>
                <Select.Option value="ORDER">Order</Select.Option>
                <Select.Option value="RECEIPT">Receipt</Select.Option>
                <Select.Option value="INSPECTION">Inspection</Select.Option>
                <Select.Option value="INVOICE">Invoice</Select.Option>
                <Select.Option value="PLAN">Plan</Select.Option>
                <Select.Option value="PLAN_REPORT">Plan Report</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="teamId"
              label="Team (optional)"
            >
              <Select placeholder="Select team" loading={teamsLoading} disabled={teamsLoading || teamsError}>
                {(teams as any[]).map((t: any) => (
                  <Select.Option key={t.id} value={t.id}>
                    {t.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="fiscalYear"
              label="Fiscal Year (optional)"
            >
              <InputNumber min={2020} max={2100} style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item name="isActive" label="Active" valuePropName="checked" initialValue>
              <Switch />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Form.Item name="steps" hidden/>
        </Row>
          <Row>
          <Form.Item name="externalWorkflow" hidden/>
        </Row>
      </Card>

      <Card title="Approval Configuration" style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label="Approval Type"
              name="approvalType"
              initialValue="EXTERNAL"
              rules={[{ required: true, message: "Please select approval type" }]}
            >
              <Radio.Group
                onChange={(e) => setApprovalType(e.target.value)}
              >
                <Radio value="LOCAL">Local Approval</Radio>
                <Radio value="EXTERNAL">External Approval Flow</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
        {approvalType === 'EXTERNAL' && (
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="Select Workflow">
                <Select
                  value={selectedWorkflow?._id}
                  onChange={(value) => setSelectedWorkflow(workflows.find(w => w._id === value) || selectedWorkflow)}
                  options={[
                    ...(selectedWorkflow && !workflows.find(w => w._id === selectedWorkflow._id) ? [{ value: selectedWorkflow._id, label: selectedWorkflow.name }] : []),
                    ...workflows.map(w => ({ value: w._id, label: w.name }))
                  ]}
                  placeholder="Choose a flow"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
        )}
        {selectedWorkflow && (
          <Row>
            <Col span={24}>
              <WorkflowDiagram flow={selectedWorkflow} />
            </Col>
          </Row>
        )}
      </Card>

      {approvalType === 'LOCAL' && (
        <Card
          title="Approval Steps"
          extra={
            <Space>
              <Button icon={<SortAscendingOutlined />} onClick={sortByOrder}>
                Sort by Order
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={addStep}>
                Add Step
              </Button>
            </Space>
          }
        >
        <Table
          dataSource={sortedSteps}
          columns={columns}
          rowKey={(_r, idx) => String(idx)}
          pagination={false}
          locale={{ emptyText: "No steps added yet" }}
          footer={() => (
            <div>
              {steps?.length ? (
                <Space wrap>
                  {sortedSteps.map((s) => (
                    <Tag key={`${s.roleSlug}-${s.order}`} color="blue">
                      {s.order}: {s.stepName || s.roleSlug}
                    </Tag>
                  ))}
                </Space>
              ) : (
                <span style={{ color: "#888" }}>Add steps to define the serial approval flow.</span>
              )}
            </div>
          )}
        />
        </Card>
      )}

      {/* Modal for adding/editing steps */}
      <Modal
        title={editingStepIndex !== null ? "Edit Approval Step" : "Add Approval Step"}
        visible={isStepModalVisible}
        onOk={saveStep}
        onCancel={() => {
          setIsStepModalVisible(false);
          stepForm.resetFields();
          setEditingStepIndex(null);
        }}
        okText={editingStepIndex !== null ? "Update Step" : "Add Step"}
        width={600}
      >
        <Form form={stepForm} layout="vertical">
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="roleSlug"
                label="Role Slug"
                rules={[{ required: true, message: 'Please enter role slug' }]}
              >
                <Input placeholder="e.g. TEAM_LEAD, FINANCE_MANAGER" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="stepName"
                label="Step Name"
              >
                <Input placeholder="Optional display name" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};